import * as Tone from "tone";
import { practice } from "./practice/session";
import { baseOf } from "./instruments";
import { partSound, releaseSounds } from "./instrument-sounds";

let part = null;
// What to play next. Scheduling every note of a big score takes most of a second, so an edit only
// remembers it: `start` schedules it, and so does an edit made while it's playing.
let pending = null;

let setSampler = null;
let snareSampler = null;
let tenorsSampler = null;
let bassSampler = null;
let cymbalSampler = null;
let pianoSampler = null;

export function setSamplers(setSamplerIn, snareSamplerIn, tenorsSamplerIn, bassSamplerIn, cymbalSamplerIn, pianoSamplerIn = null)
{
  pianoSampler = pianoSamplerIn;
  setSampler = setSamplerIn;
  snareSampler = snareSamplerIn;
  tenorsSampler = tenorsSamplerIn;
  bassSampler = bassSamplerIn;
  cymbalSampler = cymbalSamplerIn;
}

export function update(toneJsData, repeat, startStop) {
  pending = { toneJsData, repeat, startStop };
  if (Tone.Transport.state === "started") schedule();
}

function schedule() {
  if (!pending) return;
  const { toneJsData, repeat, startStop } = pending;
  pending = null;
  if (part) {
    part.dispose();
  }

  // A last event where the score ends, so a note held to the end isn't stopped as it starts.
  const events = [...toneJsData.notes, { time: toneJsData.duration }];
  let counter = 0;
  part = new Tone.Part((time, value) => {
    // A practice session listening for accuracy hears when each note sounds.
    if (value?.instrument) practice.expect(time, value.velocity ?? 1);
    if (value?.instrument) {
      const instrument = baseOf(value.instrument);
      if (instrument === "drumset") {
        // the value is an object which contains both the note and the velocity
        setSampler.triggerAttackRelease(value.note, "4", time, value.velocity);
      } else if (instrument === "snare") {
        // the value is an object which contains both the note and the velocity
        snareSampler.triggerAttackRelease(value.note, "8n", time, value.velocity);
      } else if(instrument === 'tenors') {
        tenorsSampler.triggerAttackRelease(value.note, "8n", time, value.velocity);
      } else if(instrument === 'bass') {
        bassSampler.triggerAttackRelease(value.note, "8n", time, value.velocity);
      } else if(instrument === 'cymbals') {
        cymbalSampler.triggerAttackRelease(value.note, "8n", time, value.velocity);
      } else if (instrument === "piano") {
        // A chord, held for its written length.
        pianoSampler?.triggerAttackRelease(value.note, value.duration, time, value.velocity);
      } else {
        // Every other instrument: sampled, and silent until its samples have loaded.
        partSound(instrument, value.sound)?.triggerAttackRelease(value.note, value.duration ?? 0.2, time, value.velocity);
      }
    } else {
      //It's a rest
    }

    if(++counter === events.length && !part.loop) {
      startStop();
      counter = 0;
    }
  }, events);

  // A practice loop, from where its first measure first plays to where its last one first ends.
  if('start' in repeat && 'end' in repeat) {
    const first = toneJsData.measureTimes?.[repeat.start], last = toneJsData.measureTimes?.[repeat.end];
    if (first && last) {
      part.loopStart = first.start;
      part.loopEnd = last.end;
    } else {
      const durationPerMeasure = toneJsData.duration / toneJsData.numMeasures;
      part.loopStart = durationPerMeasure * repeat.start;
      part.loopEnd = durationPerMeasure * (repeat.end + 1);
    }
    part.loop = true;
  } 

  part.start(0);

}

export function clearPlayback() {
  pending = null;
  if (part) { part.dispose(); part = null; }
}

export async function start() {
  await Tone.start();
  schedule();
  Tone.Transport.start();
}

export async function stop() {
  Tone.Transport.stop();
  releaseSounds();
}
